/**
 * The referral lifecycle: state transitions, consent gates, and the feedback each
 * transition returns to the model.
 *
 * This file is where the product's compounding advantage lives. Every public
 * dataset can tell us whether a patient's chart *looks* like a trial's criteria.
 * Only this state machine can tell us that a real coordinator looked at a real
 * candidate and said "no, the ejection fraction was measured before the last
 * hospitalisation" — a label no amount of pretraining supplies. So transitions are
 * not merely bookkeeping; they are the training-data collection mechanism, and the
 * schema treats the reason for a transition as a required field rather than a note.
 */
import { z } from 'zod'
import { CandidateStateSchema, CONSENT_REQUIRED_TO_CONTACT, canTransition, type CandidateState } from './vocab.js'
import { IsoInstantSchema, idSchema } from './zod-util.js'
import type { CandidateId, CriterionId, UserId } from './ids.js'

/**
 * Why a candidate was dismissed or failed screening.
 *
 * A closed vocabulary rather than free text, because these are the labels that
 * train the next model. `CRITERION_NOT_MET` additionally carries which criterion,
 * which turns one dismissal into a supervised example at the level the model
 * actually predicts.
 */
export const DismissReasonSchema = z.enum([
  'CRITERION_NOT_MET', // a specific criterion genuinely fails — requires criterionId
  'DATA_WAS_WRONG', // our record was stale or mis-extracted — a model/data bug
  'CLINICALLY_INAPPROPRIATE', // eligible on paper, wrong for this patient
  'PATIENT_ALREADY_ON_TRIAL',
  'PATIENT_NOT_INTERESTED',
  'SITE_TOO_FAR',
  'SITE_FULL_OR_CLOSED',
  'TRIAL_CLOSED',
  'DUPLICATE_CANDIDATE',
  'COMPETING_TRIAL_PREFERRED', // a better trial exists for this patient
  'OTHER',
])
export type DismissReason = z.infer<typeof DismissReasonSchema>

/**
 * Reasons that indicate *our* error rather than a true ineligibility.
 *
 * Split out because they must be counted separately: `DATA_WAS_WRONG` is a defect
 * in the pipeline, while `CRITERION_NOT_MET` is the system working correctly and
 * the patient simply not qualifying. Averaging them into one "rejection rate"
 * would hide the only one of the two we can fix.
 */
export const OUR_FAULT_REASONS: readonly DismissReason[] = ['DATA_WAS_WRONG', 'DUPLICATE_CANDIDATE', 'TRIAL_CLOSED', 'SITE_FULL_OR_CLOSED']

/** One recorded transition. Append-only; the sequence is the candidate's history. */
export const ReferralEventSchema = z.object({
  candidateId: idSchema<CandidateId>('candidate id'),
  from: CandidateStateSchema,
  to: CandidateStateSchema,
  /** Who moved it. Required — an unattributed state change is not auditable. */
  actorUserId: idSchema<UserId>('user id'),
  actorRole: z.string().min(1),
  /** Required for negative transitions; optional otherwise. Enforced by `validateTransition`. */
  reason: DismissReasonSchema.optional(),
  /** The specific criterion that failed, when `reason` is `CRITERION_NOT_MET`. */
  criterionId: idSchema<CriterionId>('criterion id').optional(),
  note: z.string().default(''),
  at: IsoInstantSchema,
})
export type ReferralEvent = z.infer<typeof ReferralEventSchema>

/** States whose transition requires a reason, because they are how a candidate dies. */
export const NEGATIVE_STATES: readonly CandidateState[] = ['DISMISSED', 'PATIENT_DECLINED', 'SCREEN_FAILED', 'WITHDRAWN']

/**
 * Full validation of a proposed transition: legality, required reason, required
 * permission, and consent.
 *
 * One function rather than four scattered checks, called by the Cloud Function
 * that owns writes to candidate state. Returns every problem at once so the UI can
 * show a complete explanation instead of one error at a time.
 */
export function validateTransition(input: {
  from: CandidateState
  to: CandidateState
  reason?: DismissReason
  criterionId?: string
  /** Consent scopes currently `GRANTED` for this patient. */
  grantedConsents: readonly string[]
  /** Whether the actor holds the permission this transition needs. */
  actorHasPermission: boolean
  /** Whether the actor has completed human-subjects training. */
  actorTrained: boolean
}): { ok: boolean; errors: string[] } {
  const errors: string[] = []

  if (!canTransition(input.from, input.to)) {
    errors.push(`A match cannot move from “${STATE_LABELS[input.from]}” to “${STATE_LABELS[input.to]}”.`)
  }

  if (NEGATIVE_STATES.includes(input.to) && !input.reason) {
    errors.push(`Moving a match to “${STATE_LABELS[input.to]}” requires a reason.`)
  }

  if (input.reason === 'CRITERION_NOT_MET' && !input.criterionId) {
    errors.push('Choose the specific criterion the patient did not meet.')
  }

  if (!input.actorHasPermission) {
    errors.push('You do not have permission to make this change.')
  }

  // The consent gate. Approving outreach is the point at which the system stops
  // "identifying a candidate" and starts "recruiting a subject" — a different act
  // under the HIPAA research provisions, needing training and a consent basis.
  if (input.to === 'OUTREACH_APPROVED') {
    if (!input.actorTrained) {
      errors.push('Approving outreach requires completed human-subjects research training.')
    }
    const missing = CONSENT_REQUIRED_TO_CONTACT.filter((scope) => !input.grantedConsents.includes(scope))
    if (missing.length > 0) {
      errors.push('The patient has not consented to be contacted by a study team.')
    }
  }

  return { ok: errors.length === 0, errors }
}

/**
 * The training signal a transition produces.
 *
 * `label` is the supervised target: did this candidate turn out to be genuinely
 * eligible? `weight` reflects how much to trust it — a completed screening visit
 * is near-ground-truth, a coordinator's chart-review guess is weaker, and "the
 * patient wasn't interested" says nothing about eligibility at all and gets weight
 * zero. Feeding disinterest into an eligibility model would teach it to predict
 * enthusiasm, which is a different and much less useful thing.
 */
export function trainingSignal(event: ReferralEvent): {
  label: 1 | 0 | null
  weight: number
  rationale: string
} {
  switch (event.to) {
    case 'ENROLLED':
      return { label: 1, weight: 1.0, rationale: 'Enrolled — confirmed eligible by a screening visit.' }
    case 'CONSENTED':
      return { label: 1, weight: 0.9, rationale: 'Consented after screening — very likely eligible.' }
    case 'PRESCREENED':
      return { label: 1, weight: 0.6, rationale: 'Passed chart or phone pre-screen — probably eligible.' }
    case 'SCREEN_FAILED':
      return {
        label: 0,
        weight: event.reason === 'CRITERION_NOT_MET' ? 1.0 : 0.7,
        rationale:
          event.reason === 'CRITERION_NOT_MET'
            ? 'Screen-failed on an identified criterion — the strongest negative label available.'
            : 'Screen-failed for another reason.',
      }
    case 'DISMISSED':
      if (event.reason === 'CRITERION_NOT_MET') {
        return { label: 0, weight: 0.8, rationale: 'A reviewer identified a criterion the patient does not meet.' }
      }
      if (event.reason === 'DATA_WAS_WRONG') {
        // Not an eligibility label at all — it is a data-quality defect report.
        return { label: null, weight: 0, rationale: 'Our data was wrong; this trains the extractor, not the ranker.' }
      }
      return { label: null, weight: 0, rationale: 'Dismissed for an operational reason unrelated to eligibility.' }
    case 'PATIENT_DECLINED':
      // Deliberately unlabelled: willingness is orthogonal to eligibility, and
      // learning from it would quietly bias the model against patient groups that
      // decline more often — which is precisely the population equity work is
      // trying to reach.
      return { label: null, weight: 0, rationale: 'Patient declined — says nothing about eligibility. Not used as a label.' }
    default:
      return { label: null, weight: 0, rationale: 'Intermediate state; no eligibility signal.' }
  }
}

/** Human-readable state labels, per audience. */
export const STATE_LABELS: Record<CandidateState, string> = {
  SURFACED: 'New match',
  SHORTLISTED: 'Shortlisted',
  DISMISSED: 'Dismissed',
  CLINICIAN_REVIEWED: 'Clinician reviewed',
  OUTREACH_APPROVED: 'Cleared for outreach',
  PATIENT_INTERESTED: 'Patient interested',
  PATIENT_DECLINED: 'Patient declined',
  PRESCREENED: 'Pre-screened',
  SCREENING_SCHEDULED: 'Screening scheduled',
  CONSENTED: 'Consented',
  ENROLLED: 'Enrolled',
  SCREEN_FAILED: 'Screen failed',
  WITHDRAWN: 'Withdrawn',
}

/**
 * What the patient is shown for each state.
 *
 * Separate from `STATE_LABELS` because the internal vocabulary is wrong for the
 * person it describes: "Surfaced" is meaningless, and being told you were
 * "dismissed" by an algorithm is needlessly cold. Several internal states are
 * deliberately invisible to the patient — a candidate the care team has not yet
 * reviewed should not generate hope, so it reads as nothing at all.
 */
export const PATIENT_STATE_COPY: Record<CandidateState, string | null> = {
  SURFACED: null, // not yet shown — no human has looked
  SHORTLISTED: null,
  DISMISSED: null,
  CLINICIAN_REVIEWED: 'Your care team is reviewing this study for you.',
  OUTREACH_APPROVED: 'A study team may reach out to you about this study.',
  PATIENT_INTERESTED: 'You told us you are interested. The study team will be in touch.',
  PATIENT_DECLINED: 'You told us you are not interested in this study.',
  PRESCREENED: 'You passed the first eligibility check.',
  SCREENING_SCHEDULED: 'Your screening visit is scheduled.',
  CONSENTED: 'You have signed the consent form for this study.',
  ENROLLED: 'You are enrolled in this study.',
  SCREEN_FAILED: 'This study turned out not to be a fit after the screening visit.',
  WITHDRAWN: 'You are no longer taking part in this study.',
}
